import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import Step from '../../../../../ModalStepsOutline'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectScheduleTourPreferredAgent,
  selectScheduleTourStep,
  setScheduleTourShowModal,
  setScheduleTourStep,
  setScheduleTourStepAdvanceOneStep,
  setScheduleTourPreferredAgent,
  STEPS,
  STEPS_CONST
} from '../../../../../../../../store/slices/scheduleTour'
import { Listing } from '../../../../../../../../api/gqlaxy/gql/generated/graphql'
import {
  Box,
  Flex,
  Form,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text
} from '@homebotapp/hb-react-component-catalog'
import { useState } from 'react'
import { useGetPreferredAgent } from '../../../../../../../../hooks/listings/useGetPreferredAgent'
import { selectClientId } from '../../../../../../../../store/selectors/client'
import { selectCustomerIsLoanOfficer } from '../../../../../../../../store/selectors/customerProfile'
import { useUpdatePreferredAgent } from '../../../../../../../../hooks/listings/useUpdatePreferredAgent'
import { CustomPreferredAgent } from '../../../../../RealEstateAgentCard/RealEstateAgentCard'
import { flashNotification } from '../../../../../../../../actions'
import { ScheduleTourPreferredAgentTypes } from '../../../../../../../../types/scheduleTour'

export interface AgentPreferenceProps {
  listing: Listing
}

export const MSG = defineMessages({
  preferredAgentRadioLabel: {
    id: 'ScheduleTour.AgentPreference.RadioLabel',
    defaultMessage: 'Agent preference'
  },
  listingAgent: {
    id: 'ScheduleTour.AgentPreference.YourListingAgent',
    defaultMessage: 'Listing agent'
  },
  yourPreferredAgent: {
    id: 'ScheduleTour.AgentPreference.YourPreferredAgent',
    defaultMessage: 'Your preferred agent'
  },
  addPreferredAgent: {
    id: 'ScheduleTour.AgentPreference.AddPreferredAgent',
    defaultMessage: 'Add preferred agent'
  },
  addPreferredAgentBody: {
    id: 'ScheduleTour.AgentPreference.AddPreferredAgentBody',
    defaultMessage: 'Send your questions to agent you know. We’ll save this info as an option for future questions.'
  },
  firstName: {
    id: 'ScheduleTour.AgentPreference.FirstName',
    defaultMessage: 'First name'
  },
  lastName: {
    id: 'ScheduleTour.AgentPreference.LastName',
    defaultMessage: 'Last name'
  },
  email: {
    id: 'ScheduleTour.AgentPreference.Email',
    defaultMessage: 'Email'
  }
})

export const AgentPreference = ({ listing }: AgentPreferenceProps) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const currentStep = useSelector(selectScheduleTourStep)
  const stepIndex = STEPS.indexOf(currentStep)

  const listingAgent = listing?.agent?.name ?? listing?.agent?.email
  const listingAgentCompany = listing?.brokerage?.name || ''

  const clientId = useSelector(selectClientId) as string
  const isCustomerLo = useSelector(selectCustomerIsLoanOfficer)

  const { data: preferredAgentData } = useGetPreferredAgent(
    {
      input: {
        clientId
      }
    },
    {
      enabled: isCustomerLo
    }
  )

  const { mutateAsync: updatePreferredAgent } = useUpdatePreferredAgent()
  const hasPreferredAgent = !!preferredAgentData?.preferredAgent?.agentEmail
  const preferredAgent = preferredAgentData?.preferredAgent

  const agentSelectionFromState = useSelector(selectScheduleTourPreferredAgent)
  const [recipientSelection, setRecipientSelection] = useState<ScheduleTourPreferredAgentTypes | null>(
    agentSelectionFromState
  )
  const [customPreferredAgent, setCustomPreferredAgent] = useState<CustomPreferredAgent>({})
  const [emailIsValid, setEmailIsValid] = useState(true)

  const updateAgent = async () => {
    let updated = true
    try {
      if (recipientSelection === 'preferredAgent' && !hasPreferredAgent) {
        if (customPreferredAgent?.email && customPreferredAgent?.firstName && customPreferredAgent?.lastName) {
          const response = await updatePreferredAgent({
            input: {
              agentEmail: customPreferredAgent?.email,
              agentFirstName: customPreferredAgent?.firstName,
              agentLastName: customPreferredAgent?.lastName
            }
          })
          setCustomPreferredAgent({})
          updated = !!response?.updatePreferredAgent?.agentEmail
        }
      }
    } catch (e) {
      updated = false
    }
    return updated
  }

  const handleSubmit = async () => {
    // True if updated or recipient selection doesn't need an update
    const agentUpdated = await updateAgent()
    if (!agentUpdated) {
      dispatch(flashNotification({ message: 'Error updating agent', messageType: 'error' }))
      return false
    }
    if (recipientSelection) {
      dispatch(setScheduleTourPreferredAgent(recipientSelection))
    }
    return dispatch(setScheduleTourStepAdvanceOneStep())
  }

  const validateEmail = () => {
    if (customPreferredAgent.email) {
      const email = customPreferredAgent.email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        setEmailIsValid(false)
        dispatch(flashNotification({ message: 'Invalid email', messageType: 'error' }))
        return
      }
      setEmailIsValid(true)
    }
  }

  return (
    <Step stepLabel='schedule-tour-preferred-agent'>
      <Step.HeaderNavSection
        currentStep={currentStep}
        steps={STEPS}
        onCancel={() => dispatch(setScheduleTourShowModal(false))}
        onBack={() => dispatch(setScheduleTourStep(STEPS_CONST[stepIndex - 1]))}
      />
      <Step.Title labelFor='contact-step-preferred-agent'>
        <FormattedMessage
          id='ScheduleTour.AgentPreference.Label'
          defaultMessage='Who do you want to contact to help you get a tour scheduled?'
        />
      </Step.Title>
      <Step.Body>
        <Form
          onSubmit={() => {}}
          defaultValues={{
            preferredAgentSelection: preferredAgent?.agentEmail ? agentSelectionFromState : 'listingAgent'
          }}
        >
          <FormControl>
            <FormLabel srOnly>{intl.formatMessage(MSG.preferredAgentRadioLabel)}</FormLabel>
            <RadioGroup
              name='preferredAgentSelection'
              onClick={e => {
                setRecipientSelection((e.target as HTMLInputElement).value as ScheduleTourPreferredAgentTypes)
              }}
            >
              <Stack direction='column'>
                <Radio value={'listingAgent'}>
                  <Heading size='3xs' variant='eyebrow' pb={0} my={0} color={'primary'} textAlign={'left'}>
                    {intl.formatMessage(MSG.listingAgent)}
                  </Heading>
                  <Text align={'left'} mb={0} size={'xs'}>
                    {listingAgent?.toUpperCase()}
                  </Text>
                  <Text align={'left'} size='3xs' color={'neutral-700'}>
                    {listingAgentCompany.toUpperCase()}
                  </Text>
                </Radio>
                <Radio value='preferredAgent'>
                  <Heading size='3xs' variant='eyebrow' pb={0} my={0} color={'primary'} textAlign={'left'}>
                    {intl.formatMessage(hasPreferredAgent ? MSG.yourPreferredAgent : MSG.addPreferredAgent)}
                  </Heading>
                  {hasPreferredAgent ? (
                    <Text
                      align={'left'}
                      size={'xs'}
                    >{`${preferredAgent?.agentFirstName?.toUpperCase()} ${preferredAgent?.agentLastName?.toUpperCase()}`}</Text>
                  ) : (
                    <Text align={'left'} size='3xs' color={'neutral-700'}>
                      {intl.formatMessage(MSG.addPreferredAgentBody)}
                    </Text>
                  )}
                </Radio>
              </Stack>
            </RadioGroup>
          </FormControl>
          {!hasPreferredAgent && (
            <Box>
              <Box mt={4}>
                {recipientSelection === 'preferredAgent' && !hasPreferredAgent && (
                  <Flex flexDirection='column' gap={3}>
                    <Flex gap={2}>
                      <Box>
                        <Flex gap={1}>
                          <Text m={0} size='xs'>
                            {intl.formatMessage(MSG.firstName)}
                          </Text>
                          <Text m={0} size='xs' color='danger' aria-hidden='true'>
                            *
                          </Text>
                        </Flex>
                        <Input
                          value={customPreferredAgent.firstName}
                          onChange={e => {
                            setCustomPreferredAgent({
                              ...customPreferredAgent,
                              firstName: e.target.value
                            })
                          }}
                          size='sm'
                          required
                          name='firstName'
                        />
                      </Box>
                      <Box>
                        <Flex gap={1}>
                          <Text m={0} size='xs'>
                            {intl.formatMessage(MSG.lastName)}
                          </Text>
                          <Text m={0} size='xs' color='danger' aria-hidden='true'>
                            *
                          </Text>
                        </Flex>
                        <Input
                          value={customPreferredAgent.lastName}
                          onChange={e => {
                            setCustomPreferredAgent({
                              ...customPreferredAgent,
                              lastName: e.target.value
                            })
                          }}
                          required
                          size='sm'
                          name='lastName'
                        />
                      </Box>
                    </Flex>
                    <Box>
                      <Flex gap={1}>
                        <Text m={0} size='xs'>
                          {intl.formatMessage(MSG.email)}
                        </Text>
                        <Text m={0} size='xs' color='danger'>
                          *
                        </Text>
                      </Flex>
                      <Input
                        type='email'
                        value={customPreferredAgent.email}
                        onBlur={validateEmail}
                        onChange={e => {
                          setCustomPreferredAgent({
                            ...customPreferredAgent,
                            email: e.target.value
                          })
                        }}
                        size='sm'
                        required
                        name='email'
                      />
                    </Box>
                  </Flex>
                )}
              </Box>
            </Box>
          )}
        </Form>
      </Step.Body>
      <Step.FooterButton
        disableButton={!recipientSelection || (recipientSelection === 'preferredAgent' && !emailIsValid)}
        onClick={() => handleSubmit()}
      >
        <FormattedMessage id='ScheduleTour.FooterButton.NextStep' defaultMessage='Next Step' />
      </Step.FooterButton>
    </Step>
  )
}
